import React from "react";
import PropTypes from "prop-types";
import LollipopPetal from "../models/LollipopPetal";
import VariantLink from "../../../components/TooltipActions/VariantLink";
import { tierLabels } from "../../../constants/ampTiers";
import Track from "../../../models/Track";
import Zygosity from "../../../components/Zygosity/Zygosity";
import getPrettyNumberFormat from "../../../helpers/getPrettyNumberFormat";
import Limiter from "../../../../../components/atoms/Limiter";
import Btn from "../../../../../components/atoms/Btn";
import PathogenicityLabel from "../../../components/PathogenicityLabel";
import ImpactLabel from "../../../components/ImpactLabel";
import PhasingGroupDetails from "./PhasingGroupDetails";

const columnCl =
  "tw-pt-6 tw-relative tw-flex-shrink-0 tw-flex-grow-333 tw-border-r border-normal";
const colCl =
  "tw-flex-shrink-0 tw-w-full tw-h-8 tw-border-b border-normal tw-flex tw-items-center tw-px-1";
const headerCl =
  "tw-absolute tw-truncate tw-top-0 tw-inset-x-0 tw-border-t tw-border-b border-normal tw-p-1 tw-pr-0 tw-overflow-hidden tw-font-semibold tw-bg-blueGray-400 tw-bg-opacity-20 color-2";

function PetalInfo({ petal, track, actions, requestClose }) {
  const { detail } = petal;
  const { variant } = detail;
  const { frequency, genes } = detail;
  const trackState = track.getState() || {};
  const { sampleDict, pathogenicityColors } = trackState;
  const { impactColors, phasingColors } = trackState;
  const { phasingGroups } = trackState;
  const acmgRules = (detail.acmgRules || []).map((x) => x.replace(/_/g, " "));
  const ampRules = (detail.ampRules || []).map((x) => x.replace(/_/g, " "));
  const tier = tierLabels[detail.tier];
  const samples = Object.entries(variant.sampleInfo || {}).map(
    ([key, val]) => ({
      ...(val || {}),
      ...(sampleDict[key] || {}),
    })
  );

  if (!detail) return null;

  return (
    <>
      <div className="tooltip-header border-normal">
        <div className="tooltip-title">
          <VariantLink variant={variant} />
        </div>
        {actions}
      </div>

      <div className="tooltip-row tw-items-center">
        <div className="tooltip-label tw-w-24">Germline Class</div>
        <PathogenicityLabel
          pathoIndex={detail.pathoIndex}
          colors={pathogenicityColors}
          isLabelShort={false}
        />
      </div>

      <div className="tooltip-row">
        <div className="tooltip-label tw-w-24">Coding Impact</div>
        {detail.codingImpactIndex === undefined ? (
          "-"
        ) : (
          <ImpactLabel
            impactIndex={detail.codingImpactIndex}
            colors={impactColors}
          />
        )}
      </div>

      <div className="tooltip-row">
        <div className="tooltip-label tw-w-24">Germline Rules</div>
        <div className="tw-max-w-80">{acmgRules.join(",  ")}</div>
      </div>
      {tier ? (
        <div className="tooltip-row">
          <div className="tooltip-label tw-w-24">Somatic Class</div>
          <div className="">{tier}</div>
        </div>
      ) : null}
      {ampRules.length ? (
        <div className="tooltip-row">
          <div className="tooltip-label tw-w-24">Somatic Rules</div>
          <div className="tw-max-w-80">{ampRules.join(", ")}</div>
        </div>
      ) : null}
      <div className="tooltip-row">
        <div className="tooltip-label tw-w-24">Frequency</div>
        <div
          data-tooltip={frequency}
          data-tooltip-class="genome-browser-small-tooltip"
        >
          {getPrettyNumberFormat(frequency)}
        </div>
        <div className="tw-flex-grow" />
      </div>

      <div className="tooltip-row">
        <div className="tooltip-label tw-w-24">Genes</div>
        <div className=" tw-whitespace-normal tw-max-w-80">
          <Limiter
            items={genes}
            limit={1}
            ExpandLink={({ remaining, toggle, isExpanded }) => (
              <Btn className="tw-mx-2 tw-text-primary link" onSubmit={toggle}>
                {isExpanded ? `show fewer` : `+ ${remaining} more`}
              </Btn>
            )}
            renderContent={({ items }) => (
              <span>{items.join(", ") || "-"}</span>
            )}
          />
        </div>
      </div>

      <div className="tw-h-2" />

      <div className="color-3 tw-text-2xs tw-flex tw-min-w-80">
        <div className="tw-pt-6 tw-relative tw-flex-grow-333 tw-border-l tw-border-r border-normal tw-overflow-hidden">
          <div
            className={headerCl}
            data-tooltip="Sample Name"
            data-tooltip-class="genome-browser-small-tooltip"
          >
            Sample Name
          </div>
          {samples.map((x) => {
            return x.analysisId === window.resultsConfig?.analysis_id ? (
              <div className={colCl} key={x.sampleName}>
                <div
                  className="tw-min-w-0 tw-truncate tw-font-bold"
                  data-tooltip={x.sampleName}
                  data-tooltip-class="genome-browser-small-tooltip"
                >
                  {x.sampleName}
                </div>
              </div>
            ) : (
              <div
                key={x.sampleName}
                className={colCl}
                data-tooltip={x.sampleName}
                data-tooltip-class="genome-browser-small-tooltip"
              >
                <a
                  href={`${window.resultsConfig.baseUrl}${x.url}?variant_id=${variant.region.clinicalSearch}`}
                  target="_blank"
                  className="tw-min-w-0 tw-truncate tw-font-bold"
                >
                  {x.sampleName}
                </a>
              </div>
            );
          })}
        </div>
        <div className={`${columnCl} tw-min-w-6`}>
          <div
            className={headerCl}
            data-tooltip="Coverage"
            data-tooltip-class="genome-browser-small-tooltip"
          >
            Coverage
          </div>
          {samples.map((x) => (
            <div key={x.sampleName} className={colCl}>
              <span
                data-tooltip={x.coverage}
                data-tooltip-class="genome-browser-small-tooltip"
              >
                {getPrettyNumberFormat(x.coverage)}
              </span>
            </div>
          ))}
        </div>

        <div className={`${columnCl} tw-min-w-6`}>
          <div
            className={headerCl}
            data-tooltip="Allelic balance"
            data-tooltip-class="genome-browser-small-tooltip"
          >
            Allelic Balance
          </div>

          {samples.map((x) => (
            <div key={x.sampleName} className={colCl}>
              <span
                data-tooltip={x.allelicBalance}
                data-tooltip-class="genome-browser-small-tooltip"
              >
                {getPrettyNumberFormat(x.allelicBalance)}
              </span>
            </div>
          ))}
        </div>
        <div className={`${columnCl} tw-min-w-6`}>
          <div
            className={headerCl}
            data-tooltip="Zygosity"
            data-tooltip-class="genome-browser-small-tooltip"
          >
            Zygosity
          </div>

          {samples.map((x) => (
            <div key={x.sampleName} className={`${colCl} tw-justify-center`}>
              <Zygosity zygosity={x.zygosity} phasingGroup={x.phasingGroup} />
            </div>
          ))}
        </div>
        {phasingGroups && phasingGroups.length ? (
          <div className={`${columnCl} tw-min-w-6`}>
            <div
              className={headerCl}
              data-tooltip="Phasing Group"
              data-tooltip-class="genome-browser-small-tooltip"
            >
              Phasing Group
            </div>

            {samples.map((x) => (
              <div key={x.sampleName} className={colCl}>
                <PhasingGroupDetails
                  track={track}
                  allowActions={Boolean(actions && requestClose)}
                  requestClose={requestClose}
                  phasingGroup={x.phasingGroup}
                  colors={phasingColors}
                  actions={actions}
                />
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}

PetalInfo.propTypes = {
  requestClose: PropTypes.func,
  track: PropTypes.instanceOf(Track).isRequired,
  petal: PropTypes.instanceOf(LollipopPetal).isRequired,
  actions: PropTypes.node,
};

PetalInfo.defaultProps = {
  actions: null,
  requestClose: null,
};

export default React.memo(PetalInfo);
